import React, { useState, useEffect } from "react";
import { Divider, Typography, Flex, Form, message } from "antd";
import {
  CustomButton,
  CustomSelect,
  CustomTitle,
} from "../../../components/index.js";
import { CustomParagraph } from "../../../components/index.js";
import { useToken } from "../../../common/helper/index.js";
import { useSpin } from "../../../components/SpinContext.js";
import { getTeamsDDLOFFeature } from "../../../common/utils/dropDown.js";
import { SaveAlertThresholds } from "../../../api's/settings.js";
import { useSelector } from "react-redux";
import { GetAlertThresholds } from "../../../api's/settings.js";
import { useNavigate } from "react-router-dom";
import { alertThresholdsConfig } from "./AlertThresholdConfig.js";
import refreshIcon from "../../../assets/images/refreshIcon.svg";
import SensorInfoIcon from "../../../assets/images/SensorInfoIcon.jsx";
import SensorInfoIconDisabled from "../../../assets/images/SensorInfoIconDisabled.jsx";
import ThresholdInput from "./AlertThresholdInput.jsx";
const { Text } = Typography;
const AlertThresholds = ({ title }) => {
  const { token, removeToken, decodedUserToken } = useToken();
  const isCompanyOwner = decodedUserToken()?.is_company_owner;
  const navigate = useNavigate();
  const [patchedConfig, setPatchedConfig] = useState(alertThresholdsConfig);
  const { showSpin, hideSpin } = useSpin();
  const [teamsOptions, setTeamsOptions] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState([]);
  const teams = useSelector((state) => state.teams.list);
  const [buttonsDisabled, setButtonsDisabled] = useState(false);
  const [defaultThresholds, setDefaultThresholds] = useState({});
  const [allTeamThresholds, setAllTeamThresholds] = useState({});
  const [setupSensor, setSetupSensor] = useState(false);
  const [thresholds, setThresholds] = useState({});
  const setupSensorRuleIds = alertThresholdsConfig
    .flatMap((group) => group.rules)
    .filter((rule) => rule.showSensorInfoIconDisabled)
    .map((rule) => rule.id);

  const patchThresholdValues = (config, apiData) => {
    const updated = JSON.parse(JSON.stringify(config));
    const setupSensor = apiData?.setup_sensor;

    updated.forEach((group) => {
      group.rules.forEach((rule) => {
        const key = rule.id;
        if (Object.prototype.hasOwnProperty.call(apiData, key)) {
          rule.defaultValue = apiData[key];
        }

        if (setupSensorRuleIds.includes(rule.id)) {
          rule.showSetupSensorButton = !!setupSensor;
          rule.infoIconImage = setupSensor
            ? SensorInfoIconDisabled
            : SensorInfoIcon;
        }
      });
    });

    return updated;
  };

  const fetchThresholds = async () => {
    try {
      showSpin();

      let query = "";
      if (selectedTeam === "all") {
        query = "";
      } else if (Array.isArray(selectedTeam) && selectedTeam.length > 0) {
        query = `?team=${selectedTeam[0]}`;
      } else if (typeof selectedTeam === "string") {
        query = `?team=${selectedTeam}`;
      }

      const response = await GetAlertThresholds(token, query);
      const fetchedData = response?.data?.data?.data;
      setSetupSensor(fetchedData?.setup_sensor);

      if (fetchedData) {
        const patched = patchThresholdValues(
          alertThresholdsConfig,
          fetchedData
        );
        setPatchedConfig(patched);

        const newThresholds = {};
        patched.forEach((group) => {
          group.rules.forEach((rule) => {
            newThresholds[rule.id] = rule.defaultValue;
          });
        });

        setThresholds(newThresholds);
        if (!query) {
          console.log({ newThresholds });
          setAllTeamThresholds(newThresholds);
        }
      }
    } catch (error) {
      unauthorize(error);
    } finally {
      hideSpin();
    }
  };

  useEffect(() => {
    getTeamsDDLOFFeature(token, "Alert Thresholds").then((response) => {
      if (response?.length) {
        const transformed = response.map((team) => ({
          label: team.name,
          value: team.id,
          buttonDisabled: !(
            team.permissions?.is_create || team.permissions?.is_update
          ),
        }));

        const finalOptions = isCompanyOwner
          ? [{ label: "All teams", value: "all" }, ...transformed]
          : [...transformed];

        setTeamsOptions(finalOptions);
      }
    });
  }, [token, isCompanyOwner]);

  useEffect(() => {
    fetchThresholds();
  }, [selectedTeam]);

  const unauthorize = (status) => {
    if (status === 401) {
      removeToken();
      navigate("/login");
    }
  };
  const handleSave = async () => {
    const payload = { reset: false };
    if (selectedTeam === "all") {
      payload.all_team = true;
    } else if (selectedTeam) {
      payload.all_team = false;
      payload.team = selectedTeam;
    }

    for (const [key, value] of Object.entries(thresholds)) {
      if (key === "weather_event_impact_expected") {
        const digit = String(value).match(/\d+/)?.[0];
        payload[key] = digit ? `L${digit}` : null;
      } else {
        payload[key] = value ?? null;
      }
    }
    try {
      showSpin();
      const response = await SaveAlertThresholds(token, payload);
      const resData = response?.data;

      if (resData?.data?.is_success) {
        message.success(
          resData?.data?.message || "Thresholds saved successfully"
        );
        fetchThresholds();
      } else {
        message.error(resData?.data?.message || "Failed to save thresholds");
      }
    } catch (error) {
      unauthorize(error);
    } finally {
      hideSpin();
    }
  };

  const handleReset = async () => {
    const payload = { reset: true };

    if (selectedTeam === "all") {
      payload.all_team = true;
    } else if (selectedTeam) {
      payload.all_team = false;
      payload.team = selectedTeam;
    }

    try {
      showSpin();
      const response = await SaveAlertThresholds(token, payload);
      const resData = response?.data;

      if (resData?.data?.is_success) {
        message.success(
          resData?.data?.message || "Thresholds reset to default"
        );
        fetchThresholds();
      } else {
        message.error(resData?.data?.message || "Failed to reset thresholds");
      }
    } catch (error) {
      unauthorize(error);
    } finally {
      hideSpin();
    }
  };
  useEffect(() => {
    const fetchDefaults = async () => {
      try {
        const res = await GetAlertThresholds(token, "?default=true");
        const data = res?.data?.data?.data;

        const mapped = {};
        patchThresholdValues(alertThresholdsConfig, data).forEach((group) => {
          group.rules.forEach((rule) => {
            mapped[rule.id] = rule.defaultValue;
          });
        });
        setDefaultThresholds(mapped);
      } catch (e) {
        unauthorize(e);
      }
    };

    fetchDefaults();
  }, []);

  const handleChange = (id, value) => {
    setThresholds((prev) => ({ ...prev, [id]: value }));
  };

  return (
    <div
      style={{
        overflowX: "auto",
        maxWidth: "100%",
      }}
    >
      <Flex vertical gap={8}>
        <CustomTitle
          text={title}
          fontSize={20}
          fontWeight="700"
          color="#000"
          marginBottom="0px"
        />
        <CustomParagraph
          text="Set thresholds of when certain alerts will trigger. These will be applied to all team shipments."
          fontSize="14px"
          fontWeight="400"
          lineHeight="20px"
          marginBottom="0px"
        />
      </Flex>
      {!(teams.length === 1 && teams[0]?.name?.toLowerCase() === "default") && (
        <>
          <Divider style={{ borderColor: "#D9D9D9", margin: "20px 0px" }} />

          <Flex align="center" style={{ marginBottom: 5 }}>
            <div style={{ width: 200 }}>
              <CustomTitle
                text="Team"
                fontSize={14}
                fontWeight="600"
                style={{ minWidth: "80px" }}
              />
            </div>
            <Form.Item
              name="teams"
              style={{ marginBottom: 0, width: "200px" }}
              rules={[
                { required: true, message: "Please select at least one team" },
              ]}
            >
              <CustomSelect
                placeholder="Select team"
                optionClassName="custom-form-item-team-alert-thresholds"
                value={selectedTeam}
                options={teamsOptions}
                onChange={(val) => {
                  setSelectedTeam(val);
                  const selected = teamsOptions.find((t) => t.value === val);
                  if (selected?.buttonDisabled === true) {
                    setButtonsDisabled(true);
                  } else {
                    setButtonsDisabled(false);
                  }
                }}
              />
            </Form.Item>
          </Flex>
        </>
      )}

      <Divider style={{ borderColor: "#D9D9D9", margin: "20px 0px" }} />

      {patchedConfig.map((group, index) => (
        <React.Fragment key={group.category}>
          <Flex align="flex-start">
            <Flex
              vertical
              align="flex-start"
              style={{
                width: 200,
                flexShrink: 0,
              }}
            >
              <img src={group.icon} width={28} height={28} alt="icon" />
              <Text
                strong
                style={{ fontSize: 14, marginTop: 8, lineHeight: "16px" }}
              >
                {group.category}
              </Text>
            </Flex>

            <Flex vertical style={{ flex: 1 }}>
              {group.rules.map((rule) => (
                <ThresholdInput
                setupSensorRuleIds={setupSensorRuleIds}
                  teamsOptions={teamsOptions}
                  selectedTeam={selectedTeam}
                  allTeamThresholds={allTeamThresholds}
                  thresholds={thresholds}
                  defaultThresholds={defaultThresholds}
                  disabled={buttonsDisabled}
                  setupSensor={setupSensor}
                  navigate={navigate}
                  key={rule.id}
                  rule={rule}
                  value={thresholds[rule.id]}
                  onChange={handleChange}
                />
              ))}
            </Flex>
          </Flex>

          {index < alertThresholdsConfig.length - 1 && (
            <Divider style={{ borderColor: "#D9D9D9", marginTop: "0px" }} />
          )}
        </React.Fragment>
      ))}
      <Flex
        justify="flex-start"
        gap={16}
        style={{ marginTop: 32, marginLeft: 200 }}
      >
        <CustomButton
          key="reset"
          onClick={handleReset}
          text="Reset values"
          bgColor="#E8E8E8"
          color="#000000"
          border="none"
          boxShadow={"none"}
          isFormValid={
            buttonsDisabled ||
            (selectedTeam.length === 0 && teamsOptions.length != 0)
          }
          fontSize="14px"
          fontWeight="500"
          height="36px"
          padding="15px 15px 15px 0px"
          borderRadius="8px"
          iconPosition="left"
          image={refreshIcon}
        />

        <CustomButton
          key="save"
          onClick={handleSave}
          text="Save"
          bgColor="#000000"
          color="#FFFFFF"
          isFormValid={
            buttonsDisabled ||
            (selectedTeam.length === 0 && teamsOptions.length != 0)
          }
          fontSize="14px"
          width={"107px"}
          borderRadius="8px"
          fontWeight="600"
          height="36px"
        />
      </Flex>
    </div>
  );
};

export default AlertThresholds;
